import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { pushData } from '@nykaa/data-layer';
import { ListingProduct, TipTile } from '@nykaa/listing-shared/type/product';
import { isListingItemTipTile } from '@nykaa/listing-shared/utils/product';
import { PageJourney } from '@nykaa/data-layer/store/action';
import { productListingIdName } from 'components/ListingPage/Scroll/constants';
import styled from 'App/styled';
import { State } from 'store/types';
import Product from './Product';
import TipTileCard from './TipTile';
import Widgets from 'components/Widgets';
import { DEFAULT_INLINE_WIDGET_POSITION } from './constants';
import { useIsDifferentialPricingEnabled } from 'hooks/priceReveal/differentialPrice';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  .product-tip-tile {
    width: 33.33%;
    padding: 0 10px;
    height: 440px;
  }
  .productWrapper {
    padding: 0 0.65rem;
  }
`;

const WidgetWrapper = styled.div`
  max-width: 890px;
  padding: 0px 10px;
  margin-bottom: 1.25rem;
  width: 100%;
`;

interface Props {
  products: (ListingProduct | TipTile)[];
  hasInteracted: boolean;
  journeyData?: PageJourney;
  setInteraction: React.Dispatch<React.SetStateAction<boolean>>;
  isInlineWidgetEnabled?: boolean;
  tagsOnListingEnabled?: boolean;
  tagsV2Enabled?: boolean;
}

function ProductList({
  products,
  hasInteracted,
  journeyData,
  setInteraction,
  isInlineWidgetEnabled = false,
  tagsOnListingEnabled,
  tagsV2Enabled,
}: Props) {
  const [hasPRProduct, setPRProduct] = useState(false);
  const isPro = useSelector((state: State) => state.auth.user.isPro);
  const productWrapper = 'productWrapper';

  const listingPage = useSelector((state: State) => state.categoryListing);
  const inlineWidgets = listingPage?.inlineWidgets;
  const remoteConfig = useSelector((state: State) => state.remoteConfig);

  const isDifferentialPricingEnabled = useIsDifferentialPricingEnabled();

  // skipWidgetIndex determines the position from which inline widgets should start appearing
  // Widgets at positions before this index will be skipped, allowing control over widget placement
  // Falls back to DEFAULT_INLINE_WIDGET_POSITION if no remote config is available
  const skipWidgetIndex =
    remoteConfig?.configs?.skipInlineWidgetIndex ||
    DEFAULT_INLINE_WIDGET_POSITION;

  const pageNumber = listingPage.pageNumber;

  useEffect(() => {
    if (!hasPRProduct) {
      const hasProduct = products.some((product) => !!product.prData);
      if (hasProduct) {
        pushData({ plpPriceRevealPresent: true });
      }
      setPRProduct(true);
    }
  }, [hasPRProduct, products]);

  useEffect(() => {
    const hasProduct = products?.some(
      (product) => !!product?.cohortSaleTemplate
    );
    if (hasProduct && isDifferentialPricingEnabled) {
      pushData({
        plpDifferentialPricingPresent: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let tagV2Visible = false;
    const tagV2Eligible = products?.some((product) => product.secondaryTagV2);
    if (tagsV2Enabled && tagV2Eligible) {
      tagV2Visible = true;
    }
    pushData({
      isTagV2Eligible: tagV2Eligible,
      isTagV2Visible: tagV2Visible,
    });
    return () => {
      pushData({
        isTagV2Eligible: false,
        isTagV2Visible: false,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Renders a widget at specified index if it exists and its position is valid
  // Only shows widgets at or after the skipWidgetIndex position
  const renderWidgetIfValid = React.useCallback(
    (widgetIndex: number) => {
      const currWidget = inlineWidgets?.[widgetIndex];
      const currWidgetData = currWidget && currWidget[0]?.widgetData;
      if (
        widgetIndex >= skipWidgetIndex &&
        currWidget &&
        currWidgetData?.children?.length > 0
      ) {
        return (
          <WidgetWrapper>
            <Widgets widgets={inlineWidgets[widgetIndex]} />
          </WidgetWrapper>
        );
      }
      return null;
    },
    [inlineWidgets, skipWidgetIndex]
  );

  // Renders multiple widgets for a range of consecutive indices
  // Used to render widgets in batches at specific breakpoints in the product list
  // startIndex: The highest index in the range
  // count: Number of consecutive widgets to render in descending order
  const renderWidgetsForIndices = React.useCallback(
    (startIndex: number, count: number) =>
      Array.from({ length: count }, (_, i) => {
        // Calculate widget index in ascending order (from lowest to highest)
        const widgetIndex = startIndex - count + 1 + i;
        return renderWidgetIfValid(widgetIndex);
      }),
    [renderWidgetIfValid]
  );

  return (
    <Wrapper id={productListingIdName}>
      {products.map((product: ListingProduct | TipTile, index) => {
        // Calculate the actual index based on page number (20 products per page)
        const actualIndex = (pageNumber - 1) * 20 + index;
        if (isListingItemTipTile(product)) {
          const url = product?.url || '';
          const tipTileData = {
            position: index + 1,
          };
          return (
            <div
              className="product-tip-tile"
              key={index}
            >
              <TipTileCard
                tipTile={product}
                key={url}
                tipTileData={tipTileData}
              />
            </div>
          );
        }

        const shouldShowWidget = isInlineWidgetEnabled && inlineWidgets;
        const indexRemainder = index % 3;

        return (
          <>
            {shouldShowWidget &&
              indexRemainder === 0 &&
              renderWidgetsForIndices(actualIndex, 3)}
            <Product
              key={`card-${index}`}
              size="compact"
              isPro={isPro}
              positionInList={index}
              product={product}
              customClass={productWrapper}
              hasInteracted={hasInteracted}
              setInteraction={setInteraction}
              journeyData={journeyData}
              tagsOnListingEnabled={tagsOnListingEnabled}
              tagsV2Enabled={tagsV2Enabled}
            />
            {/* Handle edge case: render any remaining widgets at the end of the product list
                Only renders if we're at the last product and it's not already the end of a row */}
            {shouldShowWidget &&
              index == products.length - 1 &&
              indexRemainder != 0 &&
              renderWidgetsForIndices(actualIndex, indexRemainder)}
          </>
        );
      })}
    </Wrapper>
  );
}

export default ProductList;
